import type { IQuote, SocketSessionManager } from '@mindverse/accessor-open';
import { useMemoizedFn } from 'ahooks';
import { type MentionsRef } from 'antd/es/mentions';
import type React from 'react';
import type { KeyboardEvent, MutableRefObject } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import { loginThirdPart } from '@/service/login';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { EVENT } from '@/utils/event';
import { GetFingerPrint } from '@/utils/fingerprint';

import ContextBasicInfo from '../../context/BasicInfo';
import type { SendMsgData } from '../../hooks/useChatSession';
import useMindStorage, { EStorageKey } from '../../hooks/useMindStorage';
import { CONTAINER_EVENT, showToast } from '../../utils/utils';
import { parseMentions } from '../chat/components/markdown/helper';
import { type FileItem, UpStatus } from './useUpload';

interface IParam {
  inChatMode: boolean;
  needLogin: boolean;
  msgLoading: boolean;
  sessionManager?: SocketSessionManager;
  upList: FileItem[];
  clearUpList(): void;
  onSendMsg?(
    data: SendMsgData,
    params?: { sessionId?: string; voiceChannel?: boolean; anonymous?: boolean }
  ): undefined | Promise<boolean>;
  handleUploadFiles(fileList: FileList | never[], e?: React.ChangeEvent<HTMLInputElement>): void;
  clearQuote?: () => void;
  quote?: IQuote;
  textareaRef: MutableRefObject<MentionsRef | undefined>;
}

interface IReturn {
  handleInputChange: (content: string) => void;
  handlePressEnter: (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  handleOnFocus: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  handleOnBlur: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  handleOnPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  isFocus: boolean;
  handleSendMsg(content: string, params?: { sessionId?: string; voiceChannel?: boolean }): void;
  handleIconSend(): void;
  inputValue: string;
  convertedValue: string;
  setConvertedValue: React.Dispatch<React.SetStateAction<string>>;
  onMentionSelect: (option: { name: string; userId: string }) => void;
  answering?: boolean;
  sendMsgLoading?: boolean;
  answerStopped?: boolean;
}

function useInput(param: IParam): IReturn {
  const {
    inChatMode,
    needLogin,
    msgLoading,
    upList,
    clearUpList,
    onSendMsg,
    handleUploadFiles,
    clearQuote,
    quote,
    sessionManager,
    textareaRef
  } = param;
  const [answering] = useMindStorage(EStorageKey.ANSWER_ING);
  const [sendMsgLoading] = useMindStorage(EStorageKey.SEND_MSG_LOADING);
  const [anonymousInitLoading, setAnonymousInitLoading] = useMindStorage(
    EStorageKey.ANONYMOUS_INIT_LOADING
  );
  const [answerStopped, setAnswerStopped] = useMindStorage(EStorageKey.ANSWER_STOPPED);
  const [inputValue, setInputValue] = useState<string>('');
  // 转换后的值，包含 <at user_id="xxx">name</at> 格式，用于发送
  const [convertedValue, setConvertedValue] = useState<string>('');
  const [isFocus, setIsFocus] = useState<boolean>(false);
  // 用于发送的时候的堆栈保存 & ctrl z 恢复
  const [lastSentValue, setLastSentValue] = useState<string>('');
  const [lastSentConvertedValue, setLastSentConvertedValue] = useState<string>('');
  const [justRestored, setJustRestored] = useState<boolean>(false);

  const { t } = useTranslation();
  const allMentionText = t('chatApp.input.mentionAll');

  const { chatType } = useContext(ContextBasicInfo);

  const [waitToSend, setWaitToSend] = useMindStorage(EStorageKey.WAIT_TO_SEND);

  const { homepageData, setHomepageData } = useHomepageContext();

  const { isLogin } = useModel('auth-model', (model) => ({
    isLogin: model.isLogin
  }));

  const handleSendMsg = useMemoizedFn(
    (value: string, params?: { sessionId?: string; voiceChannel?: boolean }) => {
      // if (needLogin) {
      //   dispatchEvent(new CustomEvent(CONTAINER_EVENT.NEED_LOGIN));

      //   return;
      // }

      const filesLoading: boolean = upList.some((file: FileItem) => {
        return file.status === UpStatus.LOADING;
      });

      // 有文件loading不允许发送
      if (filesLoading) {
        return;
      }

      const files: string[] = [];

      upList.forEach((file: FileItem) => {
        if (file.status === UpStatus.SUCCESS) {
          files.push(file.link);
        }
      });

      // 既没有成功文件 也没有内容
      if (!value.trim() && files.length === 0) {
        return;
      }

      if (anonymousInitLoading) {
        return;
      }

      // 中间步骤 loading
      if (msgLoading) {
        showToast(t('chat.errMsg.waitToFinish'));

        return;
      }

      // 回答没有结束 且 没有暂停，给提示
      if (answering && !answerStopped) {
        showToast(t('chat.errMsg.waitOrStop'));

        return;
      }

      // 直接使用 convertedValue，它已经包含 <at user_id="xxx">name</at> 格式
      const processedValue = convertedValue;

      // Save the value before clearing for Ctrl+Z recovery
      setLastSentValue(value);
      setLastSentConvertedValue(convertedValue);

      onSendMsg?.(
        {
          value: processedValue,
          files: files.length > 0 ? files : undefined,
          quote: quote ? quote : undefined
        },
        {
          ...params,
          anonymous: needLogin && homepageData?.fpAccountExists
        }
      )?.then((sended) => {
        if (sended) {
          setAnswerStopped(false);
          setInputValue('');
          setConvertedValue('');
          clearQuote?.();
          setIsFocus(true);
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 200);
          clearUpList();
        }
      });
    }
  );

  const handleWaitToSend = useCallback(
    (value: string, sessionId?: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const files: string[] = [];

        upList.forEach((file: FileItem) => {
          if (file.status === UpStatus.SUCCESS) {
            files.push(file.link);
          }
        });

        // 既没有成功文件 也没有内容
        if (!value.trim() && files.length === 0) {
          reject();

          return;
        }

        // 直接使用 convertedValue，它已经包含 <at user_id="xxx">name</at> 格式
        onSendMsg?.(
          {
            value: convertedValue,
            files: files.length > 0 ? files : undefined,
            quote: quote ? quote : undefined
          },
          { sessionId }
        )?.then((sended) => {
          setWaitToSend(false);

          if (sended) {
            setInputValue('');
            setConvertedValue('');
            clearQuote?.();
            setIsFocus(true);
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 200);
            clearUpList();
            resolve();
          }
        });
      });
    },
    [upList, convertedValue, onSendMsg, quote, setWaitToSend, clearQuote, clearUpList, textareaRef]
  );
  const handlePressEnter = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      // 中文输入法回车
      // https://www.w3.org/TR/uievents/#determine-keydown-keyup-keyCode
      if (event.keyCode === 229) {
        return;
      }

      if (event.key !== 'Enter') {
        return;
      }

      // 触发换行
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        return;
      }

      event.preventDefault();
      handleSendMsg((event.target as HTMLInputElement).value);
    },
    [handleSendMsg]
  );

  const handleIconSend = useCallback(() => {
    handleSendMsg(inputValue);
  }, [inputValue, handleSendMsg]);

  // 保存最新的 inputValue，用于在 onMentionSelect 中获取
  const inputValueRef = useRef(inputValue);

  inputValueRef.current = inputValue;

  // 保存最新的 convertedValue，用于在 handleInputChange 中获取
  const convertedValueRef = useRef(convertedValue);

  convertedValueRef.current = convertedValue;

  const handleInputChange = useCallback((content: string) => {
    // 检测 @userId::name 格式并处理
    const mentionPattern = /@([^@\s]+)::([^\s]+)/g;
    let match;
    let processedContent = content;
    // 基于之前的 convertedValue，保留已有的 <at> 标签
    let processedConverted = convertedValueRef.current;

    // 收集所有新的 mention
    const newMentions: { fullMatch: string; userId: string; name: string }[] = [];

    while ((match = mentionPattern.exec(content)) !== null) {
      newMentions.push({
        fullMatch: match[0], // @userId::name
        userId: match[1],
        name: match[2]
      });
    }

    // 如果有新的 @userId::name 格式
    if (newMentions.length > 0) {
      newMentions.forEach(({ fullMatch, userId, name }) => {
        const plainMention = `@${name}`;
        const atTag =
          userId === 'all' ? '<at user_id="all"></at>' : `<at user_id="${userId}">${name}</at>`;

        // 在 inputValue 中替换为 @name
        processedContent = processedContent.replace(fullMatch, plainMention);
        // 在 convertedValue 中也替换（如果存在的话）
        processedConverted = processedConverted.replace(fullMatch, atTag);
      });

      // 同步 convertedValue 中的普通文本变化
      // 先将 processedConverted 中的 <at> 标签提取出来，然后与 processedContent 对比
      processedConverted = syncMentionChangesWithNew(
        processedConverted,
        processedContent,
        newMentions
      );

      setInputValue(processedContent);
      inputValueRef.current = processedContent;
      setConvertedValue(processedConverted);
    } else {
      // 普通输入，需要同步更新 convertedValue
      // 但要保持已有的 <at> 标签不变
      setInputValue(content);
      inputValueRef.current = content;
      syncConvertedValue(content);
    }
  }, []);

  // 同步 mention 变化，同时处理新增的 mention
  const syncMentionChangesWithNew = (
    prevConverted: string,
    newInputValue: string,
    newMentions: { fullMatch: string; userId: string; name: string }[]
  ): string => {
    // 解析 prevConverted 中的所有 <at> 标签
    const atTagRegex = /<at\s+user_id="([^"]*)">([^<]*)<\/at>/g;
    const existingMentions: { fullTag: string; name: string; userId: string }[] = [];
    let match;

    while ((match = atTagRegex.exec(prevConverted)) !== null) {
      existingMentions.push({
        fullTag: match[0],
        userId: match[1],
        name: match[1] === 'all' ? allMentionText : match[2]
      });
    }

    // 添加新的 mention
    newMentions.forEach(({ userId, name }) => {
      existingMentions.push({
        fullTag:
          userId === 'all' ? '<at user_id="all"></at>' : `<at user_id="${userId}">${name}</at>`,
        userId,
        name
      });
    });

    // 找出 newInputValue 中所有 @name 的位置
    const allAtMentions: { name: string; index: number }[] = [];
    const atRegex = /@([^\s@]+)/g;

    while ((match = atRegex.exec(newInputValue)) !== null) {
      allAtMentions.push({
        name: match[1],
        index: match.index
      });
    }

    // 按位置顺序匹配
    let result = '';
    let lastIndex = 0;
    let mentionIdx = 0;

    allAtMentions.forEach((atMention) => {
      // 添加 @name 之前的文本
      result += newInputValue.slice(lastIndex, atMention.index);

      // 查找是否有匹配的 <at> 标签（同名且还未使用）
      const matchingMention = existingMentions.find(
        (m, idx) => idx >= mentionIdx && m.name === atMention.name
      );

      if (matchingMention) {
        result += matchingMention.fullTag;
        mentionIdx = existingMentions.indexOf(matchingMention) + 1;
      } else {
        result += `@${atMention.name}`;
      }

      lastIndex = atMention.index + 1 + atMention.name.length;
    });

    result += newInputValue.slice(lastIndex);

    return result;
  };

  // 同步 convertedValue，保持 <at> 标签不变
  const syncConvertedValue = useCallback((newInputValue: string) => {
    setConvertedValue((prevConverted) => {
      // 如果之前没有 mention，直接同步
      if (!prevConverted.includes('<at ')) {
        return newInputValue;
      }

      // 有 mention，需要智能同步
      // 策略：找出 inputValue 和 newInputValue 的差异，应用到 convertedValue
      // 简化处理：如果是删除操作，检查是否删除了 mention
      return syncMentionChanges(prevConverted, newInputValue);
    });
  }, []);

  // 同步 mention 变化
  // 策略：按位置匹配，而不是按名字
  const syncMentionChanges = (prevConverted: string, newInputValue: string): string => {
    // 解析 prevConverted 中的所有 <at> 标签及其位置
    const atTagRegex = /<at\s+user_id="([^"]*)">([^<]*)<\/at>/g;
    const mentions: { fullTag: string; name: string; userId: string; index: number }[] = [];
    let match;

    while ((match = atTagRegex.exec(prevConverted)) !== null) {
      mentions.push({
        fullTag: match[0],
        userId: match[1],
        name: match[1] === 'all' ? allMentionText : match[2],
        index: match.index
      });
    }

    // 找出 newInputValue 中所有 @name 的位置
    const allAtMentions: { name: string; index: number }[] = [];
    const atRegex = /@([^\s@]+)/g;

    while ((match = atRegex.exec(newInputValue)) !== null) {
      allAtMentions.push({
        name: match[1],
        index: match.index
      });
    }

    // 按位置顺序匹配：尝试将 prevConverted 中的 <at> 标签与 newInputValue 中的 @name 匹配
    // 如果名字相同，则保留 <at> 标签
    let result = '';
    let lastIndex = 0;
    let mentionIdx = 0;

    allAtMentions.forEach((atMention) => {
      // 添加 @name 之前的文本
      result += newInputValue.slice(lastIndex, atMention.index);

      // 查找是否有匹配的 <at> 标签（同名且还未使用）
      const matchingMention = mentions.find(
        (m, idx) => idx >= mentionIdx && m.name === atMention.name
      );

      if (matchingMention) {
        // 找到匹配的 <at> 标签，使用它
        result += matchingMention.fullTag;
        mentionIdx = mentions.indexOf(matchingMention) + 1;
      } else {
        // 没有匹配的 <at> 标签，保留原始的 @name
        result += `@${atMention.name}`;
      }

      lastIndex = atMention.index + 1 + atMention.name.length; // +1 for @
    });

    // 添加最后一个 @name 之后的文本
    result += newInputValue.slice(lastIndex);

    return result;
  };

  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const { clipboardData } = e;
      const { files } = clipboardData;

      // 如果有文件，触发上传
      if (files.length) {
        if (needLogin) {
          return;
        }

        e.preventDefault();

        handleUploadFiles(files);
      }
    },
    [handleUploadFiles, needLogin]
  );

  useEffect(() => {
    function sendCb(e: Event) {
      const text = (e as CustomEvent).detail;

      if (typeof text === 'string') {
        handleSendMsg(text);
      }
    }

    window.addEventListener(CONTAINER_EVENT.SEND_MESSAGE, sendCb);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.SEND_MESSAGE, sendCb);
    };
  }, [handleSendMsg]);

  useEffect(() => {
    const cb = (e: Event) => {
      const event = e as CustomEvent;
      const text = event.detail;

      setInputValue(text);
      setConvertedValue(text);

      setWaitToSend(true);
    };

    addEventListener(CONTAINER_EVENT.SEND_NOTE_SUGGESTION_MESSAGE, cb);

    return () => {
      removeEventListener(CONTAINER_EVENT.SEND_NOTE_SUGGESTION_MESSAGE, cb);
    };
  }, []);

  // TODO: wait to send 逻辑改掉
  useEffect(() => {
    const sendUnsendMsg = (e: Event) => {
      const sessionId = (e as CustomEvent).detail;

      if (waitToSend && inputValue) {
        // 100ms后发送
        // 1. 等待开场白动画
        // 2. 现在发送都直接进消息，会触发消息receive回调，如果不延时会导致loadingMsgId设置不准确
        setTimeout(() => {
          handleWaitToSend(inputValue, sessionId);
        }, 100);
      }
    };

    addEventListener(CONTAINER_EVENT.ANSWERED, sendUnsendMsg);

    return () => {
      removeEventListener(CONTAINER_EVENT.ANSWERED, sendUnsendMsg);
    };
  }, [handleWaitToSend, inputValue, waitToSend]);

  useEffect(() => {
    function cb(e: Event) {
      const text = (e as CustomEvent).detail;

      if (typeof text === 'string') {
        setInputValue(text);
        setConvertedValue(text);
      }
    }

    window.addEventListener(CONTAINER_EVENT.SET_INPUT_VALUE, cb);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.SET_INPUT_VALUE, cb);
    };
  }, []);

  // 监听插入 mention 事件
  useEffect(() => {
    function handleInsertMention(e: Event) {
      const mentionText = (e as CustomEvent).detail;

      if (typeof mentionText === 'string') {
        // 在光标位置插入 mention
        const textarea = textareaRef.current?.textarea;

        if (textarea) {
          const start = textarea.selectionStart || 0;
          const end = textarea.selectionEnd || 0;
          const currentValue = inputValueRef.current;

          // 在光标位置插入 mention，前后加空格
          const beforeCursor = currentValue.slice(0, start);
          const afterCursor = currentValue.slice(end);
          const newValue = `${beforeCursor}${mentionText} ${afterCursor}`;

          // 更新输入值
          handleInputChange(newValue);

          // 设置光标位置到插入的 mention 之后
          setTimeout(() => {
            const newCursorPos = start + mentionText.length + 1;

            textarea.setSelectionRange(newCursorPos, newCursorPos);
            textarea.focus();
          }, 0);
        }
      }
    }

    window.addEventListener(EVENT.INSERT_MENTION, handleInsertMention);

    return () => {
      window.removeEventListener(EVENT.INSERT_MENTION, handleInsertMention);
    };
  }, [textareaRef, handleInputChange]);

  const handleOnFocus: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> =
    useMemoizedFn(() => {
      setIsFocus(true);
    });

  const handleOnBlur: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement> =
    useCallback(() => {
      setIsFocus(false);
    }, []);

  // 处理 mention 选择
  // 现在 mention 的转换在 handleInputChange 中通过检测 @userId::name 格式来处理
  // 这个函数保留是为了兼容 MultimodalInput 的接口，但不需要做任何事情
  const onMentionSelect = useCallback((_option: { name: string; userId: string }) => {
    // 不需要做任何事情，handleInputChange 会处理 @userId::name 格式
  }, []);

  // 匿名登录模式下
  useEffect(() => {
    const fp = new GetFingerPrint();

    // TODO: 现在是用homepageData的有无判断；后续得用type
    if (!!homepageData && !isLogin && !homepageData.fpAccountExists) {
      setAnonymousInitLoading(true);
      loginThirdPart({ refPlatform: 'fp', idToken: fp.value || '' })
        .then((res) => {
          if (res.data.code !== 0) {
            throw new Error(res.data.message);
          }

          setHomepageData((pre) => {
            return {
              ...pre,
              visitUserId: res.data.data.userId,
              fpAccountExists: true
            };
          });

          sessionManager?.checkConnect();
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setAnonymousInitLoading(false);
        });
    }
  }, [homepageData, isLogin, sessionManager, setAnonymousInitLoading, setHomepageData]);

  // input 聚焦
  useEffect(() => {
    function inputFocus() {
      textareaRef.current?.focus();
    }

    if (inChatMode && chatType == 'normal') {
      inputFocus();
    }

    window.addEventListener(CONTAINER_EVENT.CHAT_INPUT_FOCUS, inputFocus);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.CHAT_INPUT_FOCUS, inputFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // input 失焦
  useEffect(() => {
    function inputBlur() {
      textareaRef.current?.blur();
    }

    window.addEventListener(CONTAINER_EVENT.CHAT_INPUT_BLUR, inputBlur);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.CHAT_INPUT_BLUR, inputBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore input value when send fails
  // 注意：事件传递的是 convertedValue（包含 <at> 标签），需要转换回 @name 格式
  useEffect(() => {
    function restoreInputValue(e: Event) {
      const text = (e as CustomEvent).detail;

      if (typeof text === 'string' && text) {
        // text 是 convertedValue 格式（包含 <at> 标签）
        // 需要转换为 @name 格式作为 inputValue
        const plainText = parseMentions(text, t);

        setInputValue(plainText);
        setConvertedValue(text);
        setLastSentValue('');
        setLastSentConvertedValue('');
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }
    }

    window.addEventListener(CONTAINER_EVENT.RESTORE_INPUT_VALUE, restoreInputValue);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.RESTORE_INPUT_VALUE, restoreInputValue);
    };
  }, [textareaRef]);

  // Ctrl+Z to restore last sent value when input is empty
  useEffect(() => {
    function handleKeyDown(e: globalThis.KeyboardEvent) {
      // Restore last sent value when input is empty
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !inputValue && lastSentValue) {
        e.preventDefault();
        setInputValue(lastSentValue);
        setConvertedValue(lastSentConvertedValue);
        setLastSentValue('');
        setLastSentConvertedValue('');
        setJustRestored(true);
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);

        return;
      }

      // Block subsequent Ctrl+Z after restore to prevent browser undo from interfering
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && justRestored) {
        e.preventDefault();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [inputValue, lastSentValue, lastSentConvertedValue, justRestored, textareaRef]);

  // Reset justRestored flag when user types new content
  useEffect(() => {
    if (justRestored && inputValue !== lastSentValue) {
      setJustRestored(false);
    }
  }, [inputValue, justRestored, lastSentValue]);

  return {
    handleOnFocus,
    handleOnBlur,
    handleInputChange,
    handlePressEnter,
    handleOnPaste,
    setInputValue,
    handleIconSend,
    handleSendMsg,
    sendMsgLoading,
    inputValue,
    convertedValue,
    setConvertedValue,
    onMentionSelect,
    answering,
    answerStopped,
    isFocus
  };
}

export default useInput;
